import { type PayloadAction, createSlice } from '@reduxjs/toolkit';

import {
  SDChatUpdateWsMapperFromResponse,
  SDChatUpdateWsMapperToMessage,
  SDDetailedMapperFromResponse,
  SDMapperFromResponse
} from '@/components/Shared/widgets/helpers/serviceDeskMapper';
import type { SDCreateRequest } from '@/components/Shared/widgets/service-desk-create/types/form';
import type {
  SDStatus,
  TSDChatUpdateWsResponse,
  TSDFilesMapItem,
  TSDMessageCountResponse,
  TSDNewAttachment
} from '@/components/Shared/widgets/types/serviceDesk.type';
import { DictTypes } from '@/constants';
import { serviceDeskApi } from '@/integrations/app.api/service-desk.api';
import { generateRandomId } from '@/services';
import { isDictHasAll, safeKey } from '@/services/helpers/dict';

import { type TRootState } from '.';
import type { TServiceDeskStore } from '../types/store';
import { createAsyncThunk } from './create-async-thunk';

const initialState: TServiceDeskStore = {
  isLoadingAll: false,
  requests: [],
  total: 0,

  newMessageCountRequestMap: {},
  newMessagesUserBadge: 0,
  newRequestsAdminBadge: 0,
  reworkRequestsAdminBadge: 0,

  chatFilesMap: {},
  chatItems: [],
  isRequestFilesLoading: false,
  newAttachedFiles: [],
  replyChatItem: null,
  requestFiles: [],
  requestItem: null,
  scrollChat: null,
  showScrollToBottom: false
};

export type ServiceDeskFilters = {
  searchTerm?: string;
  offset: string;
  limit: string;
  moduleId?: string[];
  categoryId?: string[];
  statusId?: string[];
  filterByUser?: string;
};

export type TSendMessageServiceDeskRequest = {
  isAdmin: boolean;
  reply_msg_uuid?: string;
  requestId: string;
  comment?: string;
  files?: { name: string; base64: string }[];
};

export type TChangeStatusServiceDeskRequest = {
  isAdmin: boolean;
  requestId: string;
  status_uuid: string;
  comment?: string;
  reason_uuid?: string;
};

export const getServiceDeskRequests = createAsyncThunk(
  'serviceDesk/getServiceDeskRequests',
  async (
    {
      isAdmin,
      filters
    }: {
      isAdmin: boolean;
      filters: ServiceDeskFilters;
    },
    { getState }
  ) => {
    const store = getState() as TRootState;
    return {
      response: await serviceDeskApi.getServiceDeskRequests(isAdmin, filters),
      userId: store.userInfo.userInfo?.uuid || '',
      isAdmin: store.userInfo.userAccesses.global.isAdmin
    };
  }
);

export const getServiceDeskRequest = createAsyncThunk(
  'serviceDesk/getServiceDeskRequest',
  async ({ isAdmin, requestId }: { isAdmin: boolean; requestId: string }) => {
    return await serviceDeskApi.getServiceDeskRequest(isAdmin, requestId);
  }
);

export const createServiceDeskRequest = createAsyncThunk(
  'serviceDesk/createServiceDeskRequest',
  async (data: SDCreateRequest) => {
    return serviceDeskApi.createServiceDeskRequest(data);
  }
);

export const sendMessageServiceDeskRequest = createAsyncThunk(
  'serviceDesk/sendMessageServiceDeskRequest',
  async (data: TSendMessageServiceDeskRequest) => {
    return serviceDeskApi.sendMessageServiceDeskRequest(data);
  }
);

export const changeStatusServiceDeskRequest = createAsyncThunk(
  'serviceDesk/changeStatusServiceDeskRequest',
  async (data: TChangeStatusServiceDeskRequest) => {
    return serviceDeskApi.changeStatusServiceDeskRequest(data);
  }
);

export const startUserBadgeServiceDesk = createAsyncThunk(
  'service-desk/user-badge',
  async (userId: string) => {
    return serviceDeskApi.startUserBadgeServiceDesk(userId);
  }
);

export const startAdminBadgeNewServiceDesk = createAsyncThunk(
  'service-desk/admin-badge-new',
  async () => {
    return serviceDeskApi.startAdminBadgeNewServiceDesk();
  }
);

export const startAdminBadgeReworkServiceDesk = createAsyncThunk(
  'service-desk/admin-badge-rework',
  async () => {
    return serviceDeskApi.startAdminBadgeReworkServiceDesk();
  }
);

export const setIsReadChatItems = createAsyncThunk(
  'service-desk/set-is-read',
  async (data: { isAdmin: boolean; requestId: string }) => {
    return serviceDeskApi.setIsReadChatItems(data);
  }
);

export const getServiceDeskRequestFiles = createAsyncThunk(
  'service-desk/get-service-desk-request-files',
  async (uuid: string) => {
    return serviceDeskApi.downloadServiceDeskFiles(uuid);
  }
);

export const serviceDeskSlice = createSlice({
  name: 'serviceDeskSlice',
  initialState,
  reducers: {
    resetServiceDesk: (state) => {
      state.requests = [];
      state.total = 0;
    },
    resetServiceDeskRequest: (state) => {
      state.requestItem = null;
      state.requestFiles = [];
      state.newAttachedFiles = [];
      state.chatItems = [];
      state.isRequestFilesLoading = false;
      state.replyChatItem = null;
      state.scrollChat = null;
      state.showScrollToBottom = false;
      state.chatFilesMap = {};
    },
    setChatItems: (
      state,
      action: PayloadAction<TServiceDeskStore['chatItems']>
    ) => {
      state.chatItems = action.payload;
    },
    concatNewAttachedFiles: (
      state,
      action: PayloadAction<TSDNewAttachment[] | TSDNewAttachment>
    ) => {
      state.newAttachedFiles = state.newAttachedFiles.concat(action.payload);
    },
    deleteNewAttachedFiles: (
      state,
      action: PayloadAction<TSDNewAttachment | undefined>
    ) => {
      const payload = action.payload;
      if (!payload) {
        state.newAttachedFiles = [];
        return;
      }
      state.newAttachedFiles = state.newAttachedFiles.filter(
        (file) => file.key !== payload.key
      );
    },
    initializeChatFilesMap: (
      state,
      action: PayloadAction<{ uuid: string; fileName: string }[]>
    ) => {
      state.chatFilesMap = {
        ...state.chatFilesMap,
        ...action.payload.reduce(
          (acc, { uuid, fileName }) => {
            acc[uuid] = {
              state: 'initial',
              base64: '',
              name: fileName
            };
            return acc;
          },
          {} as Record<string, TSDFilesMapItem>
        )
      };
    },
    updateChatFilesMap: (
      state,
      action: PayloadAction<{ uuid: string; mapItem: TSDFilesMapItem }>
    ) => {
      state.chatFilesMap[action.payload.uuid] = action.payload.mapItem;
    },
    setReplyMessage: (
      state,
      action: PayloadAction<TServiceDeskStore['replyChatItem']>
    ) => {
      state.replyChatItem = action.payload;
    },
    setScrollChat: (
      state,
      action: PayloadAction<TServiceDeskStore['scrollChat']>
    ) => {
      state.scrollChat = action.payload;
    },
    setShowScrollToBottom: (state, action: PayloadAction<boolean>) => {
      state.showScrollToBottom = action.payload;
    },
    updateServiceDeskRequest: (
      state,
      action: PayloadAction<{
        response: TSDChatUpdateWsResponse;
        store: TRootState;
      }>
    ) => {
      const { response, store } = action.payload;
      const chatUpdate = SDChatUpdateWsMapperFromResponse(response);
      const { dicts } = store.dict;

      const chatItem = SDChatUpdateWsMapperToMessage(chatUpdate);

      for (const request of state.requests) {
        if (request.uuid === chatUpdate.message.supportUuid) {
          request.status.code = chatUpdate.status;
          request.supportChats.unshift(chatItem);
          break;
        }
      }

      if (
        state.requestItem &&
        state.requestItem.uuid === chatUpdate.message.supportUuid
      ) {
        state.requestItem.supportChats.unshift(chatItem);

        if (
          chatUpdate.reason &&
          isDictHasAll(dicts, [DictTypes.Statuses, DictTypes.SupportReasons])
        ) {
          const foundStatus =
            dicts[DictTypes.Statuses].getBy.uuid[chatUpdate.reason.statusUuid];
          const foundSupportReason =
            dicts[DictTypes.SupportReasons].getBy.uuid[
              safeKey(chatUpdate.reason.supportReasonsUuid)
            ];

          if (foundStatus) {
            state.requestItem.supportReasonsHistory.push({
              comment: chatUpdate.reason.comment || '',
              createdAt: chatUpdate.reason.createdAt,
              isRead: false,
              status: {
                ...foundStatus,
                code: String(foundStatus.code ?? '') as SDStatus
              },
              supportReasons: foundSupportReason
                ? {
                    code: String(foundSupportReason.code ?? ''),
                    name: foundSupportReason.name,
                    type: foundSupportReason.type,
                    uuid: foundSupportReason.uuid
                  }
                : null,
              uuid: generateRandomId()
            });
          }
        }

        state.requestItem.status.code = chatUpdate.status;
      }
    },
    updateServiceDeskBadges: (
      state,
      action: PayloadAction<TSDMessageCountResponse[]>
    ) => {
      action.payload.forEach((item) => {
        state.newMessageCountRequestMap[item.support_uuid] =
          item.newMessageCount + item.newReasonHistoryCount;
      });
      state.newMessagesUserBadge = 0;
    },
    updateServiceDeskNewRequestBadge: (
      state,
      action: PayloadAction<number>
    ) => {
      state.newRequestsAdminBadge = action.payload ?? 0;
    },
    updateServiceDeskReworkRequestBadge: (
      state,
      action: PayloadAction<number>
    ) => {
      state.reworkRequestsAdminBadge = action.payload ?? 0;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServiceDeskRequests.rejected, (state) => {
        state.isLoadingAll = false;
      })
      .addCase(getServiceDeskRequests.fulfilled, (state, action) => {
        state.isLoadingAll = false;
        state.requests =
          action.payload.response[0].data.map(SDMapperFromResponse);
        state.total = action.payload.response[0].total;

        action.payload.response[1].forEach((item) => {
          if (
            action.payload.isAdmin ||
            item.create_user === action.payload.userId
          ) {
            state.newMessageCountRequestMap[item.support_uuid] =
              item.newMessageCount + item.newReasonHistoryCount;
          }
        });
      })
      .addCase(getServiceDeskRequests.pending, (state) => {
        state.isLoadingAll = true;
      })
      .addCase(getServiceDeskRequest.rejected, (state) => {
        state.requestItem = null;
      })
      .addCase(getServiceDeskRequest.fulfilled, (state, action) => {
        state.requestItem = SDDetailedMapperFromResponse(action.payload);
      })
      .addCase(getServiceDeskRequest.pending, (state) => {
        state.requestItem = null;
      })
      .addCase(startUserBadgeServiceDesk.fulfilled, (state, action) => {
        state.newMessagesUserBadge =
          action.payload.reduce(
            (total, item) =>
              total + item.newMessageCount + item.newReasonHistoryCount,
            0
          ) ?? 0;
      })
      .addCase(startUserBadgeServiceDesk.rejected, (state) => {
        state.newMessagesUserBadge = 0;
      })
      .addCase(startAdminBadgeNewServiceDesk.fulfilled, (state, action) => {
        state.newRequestsAdminBadge = action.payload ?? 0;
      })
      .addCase(startAdminBadgeNewServiceDesk.rejected, (state) => {
        state.newRequestsAdminBadge = 0;
      })
      .addCase(startAdminBadgeReworkServiceDesk.fulfilled, (state, action) => {
        state.reworkRequestsAdminBadge = action.payload ?? 0;
      })
      .addCase(startAdminBadgeReworkServiceDesk.rejected, (state) => {
        state.reworkRequestsAdminBadge = 0;
      })
      .addCase(getServiceDeskRequestFiles.pending, (state) => {
        state.requestFiles = [];
        state.isRequestFilesLoading = true;
      })
      .addCase(getServiceDeskRequestFiles.fulfilled, (state, action) => {
        state.requestFiles = action.payload;
        state.isRequestFilesLoading = false;
      })
      .addCase(getServiceDeskRequestFiles.rejected, (state) => {
        state.requestFiles = [];
        state.isRequestFilesLoading = false;
      });
  }
});

export const {
  resetServiceDesk,
  resetServiceDeskRequest,
  concatNewAttachedFiles,
  deleteNewAttachedFiles,
  initializeChatFilesMap,
  updateChatFilesMap,
  setChatItems,
  setReplyMessage,
  setScrollChat,
  setShowScrollToBottom,
  updateServiceDeskRequest,
  updateServiceDeskBadges,
  updateServiceDeskNewRequestBadge,
  updateServiceDeskReworkRequestBadge
} = serviceDeskSlice.actions;
